import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api';

export const useGetHomepage = (city?: string | null) => {
  return useQuery({
    queryKey: ['homepage', city],
    queryFn: async () => {
      const url = city ? `/public/homepage/?city=${encodeURIComponent(city)}` : '/public/homepage/';
      const { data } = await apiClient.get(url);
      return data || {};
    },
  });
};

/** A user summary as returned by the various /profiles/home/ list fields */
export interface UserDiscoverModel {
  id: string | number;
  username?: string;
  full_name?: string;
  email?: string;
  prof_pics?: string | null;
  prof_pics_url?: string | null;
  google_prof_pics?: string | null;
  user_types?: { name: string }[];
  follower_count?: number;
  city?: string;
  state?: string;
}

/** Response shape of GET /profiles/home/ */
export interface HomeData {
  top_events?: unknown[];
  djs_artists?: UserDiscoverModel[];
  upcoming_events?: unknown[];
  venues?: UserDiscoverModel[];
  organizers?: UserDiscoverModel[];
  available_locations?: string[];
  genres?: unknown[];
}

/**
 * Authenticated home feed — mirrors mobile /profiles/home/?location=
 * Returns: top_events, djs_artists, upcoming_events, venues, organizers,
 *          available_locations, genres
 */
export const useGetHomeData = (location?: string) => {
  return useQuery({
    queryKey: ['home-data', location ?? ''],
    queryFn: async (): Promise<HomeData> => {
      const qs =
        location && location !== 'All Locations' ? `?location=${encodeURIComponent(location)}` : '';
      const { data } = await apiClient.get(`/profiles/home/${qs}`);
      return (data as HomeData) || {};
    },
  });
};

export const useGetTrustedContent = (city?: string | null) => {
  return useQuery({
    queryKey: ['trusted-content', city],
    queryFn: async () => {
      const url = city
        ? `/public/trusted-content/?city=${encodeURIComponent(city)}`
        : '/public/trusted-content/';
      const { data } = await apiClient.get(url);
      const finalData = data?.success ? data.data : data;
      return finalData || {};
    },
  });
};
export interface GigFilterParams {
  search?: string;
  city?: string;
  start_date?: string;
  end_date?: string;
  budget_min?: string;
  budget_max?: string;
  event_type?: string;
  owner_id?: string | number;
  user?: string | number; // filter by creator's user_id → /gigs/?user={id}
  page?: number;
  page_size?: number;
}

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function normalizeDateFilterValue(
  value?: string,
  mode: 'start' | 'end' = 'start',
): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed || !DATE_ONLY_PATTERN.test(trimmed)) return undefined;

  // Validate the date is parseable
  const [year, month, day] = trimmed.split('-').map(Number);
  const test = new Date(year, month - 1, day);
  if (Number.isNaN(test.getTime())) return undefined;

  // Build local-time boundaries so the backend range comparison
  // covers the entire selected day, not just midnight-to-midnight UTC.
  if (mode === 'end') {
    return `${trimmed}T23:59:59`;
  }
  return `${trimmed}T00:00:00`;
}

function toGigRequestSearchParams(params: GigFilterParams): URLSearchParams {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== '' && val !== null) {
      queryParams.set(key, String(val));
    }
  });
  return queryParams;
}

export const useGetGigs = (params: GigFilterParams = {}, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['gigs', params],
    gcTime: 0,
    staleTime: 0,
    enabled: options?.enabled !== false,
    queryFn: async () => {
      const queryParams = toGigRequestSearchParams(params);
      const qs = queryParams.toString();
      const { data } = await apiClient.get(`/gigs/${qs ? `?${qs}` : ''}`);
      // Normalise: handle double-nested { results: { results: [] } }, { results: [] }, { data: [] }, plain array
      const inner = data?.results;
      const rows: unknown[] = Array.isArray(data)
        ? data
        : Array.isArray(inner)
          ? inner
          : Array.isArray((inner as Record<string, unknown>)?.results)
            ? (inner as Record<string, unknown>).results
            : Array.isArray(data?.data)
              ? data.data
              : [];
      return {
        count: data?.count ?? rows.length,
        next: data?.next ?? null,
        previous: data?.previous ?? null,
        results: rows,
        requestSignature: qs,
      };
    },
  });
};

export const useGetPublicGigs = (params: GigFilterParams = {}, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['gigs', 'public', params],
    gcTime: 0,
    staleTime: 0,
    enabled: options?.enabled !== false,
    queryFn: async () => {
      const queryParams = toGigRequestSearchParams(params);
      const qs = queryParams.toString();
      const { data } = await apiClient.get(`/public/gigs/${qs ? `?${qs}` : ''}`);
      // API may return double-nested: { count, next, results: { success, results: [...] } }
      const inner = data?.results;
      const rows: unknown[] = Array.isArray(data)
        ? data
        : Array.isArray(inner)
          ? inner
          : Array.isArray((inner as Record<string, unknown>)?.results)
            ? (inner as Record<string, unknown>).results
            : Array.isArray(data?.data)
              ? data.data
              : [];
      return {
        count: data?.count ?? rows.length,
        next: data?.next ?? null,
        previous: data?.previous ?? null,
        results: rows,
        requestSignature: qs,
      };
    },
  });
};

export const useGetGig = (gigId: string) => {
  return useQuery({
    queryKey: ['gig', gigId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/gigs/${gigId}/`);
      // Unwrap { success, data: {...} } envelope if present
      return data?.data ?? data;
    },
    enabled: !!gigId,
  });
};

export const useGetPublicGig = (gigId?: string) => {
  return useQuery({
    queryKey: ['gig', 'public', gigId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/public/gigs/${gigId}/`);
      // Response: { success: true, gig: { ... } }
      return data?.gig ?? data?.data ?? data?.results ?? data;
    },
    enabled: !!gigId,
  });
};

// ─── Gig Applications ─────────────────────────────────────────────────────

export type GigApplicationStatus = 'pending' | 'accepted' | 'rejected';
export type GigApplicationDatePreset = 'today' | 'this_week' | 'this_month' | 'custom';

export interface GigApplicationsFilterParams {
  status?: GigApplicationStatus;
  date_preset?: GigApplicationDatePreset;
  start_date?: string;
  end_date?: string;
  gig_uuid?: string;
  timezone?: string;
  page?: number;
  page_size?: number;
}

export interface GigApplicationListItem {
  application_uuid: string;
  status: GigApplicationStatus;
  created_at: string;
  applicant: { id: number; username: string; full_name: string };
  gig: { gig_uuid: string; title: string; city: string; state: string; country: string };
}

export interface GigApplicationDetail {
  application_uuid: string;
  status: GigApplicationStatus;
  applicant_email: string;
  message: string;
  created_at: string;
  updated_at: string;
  applicant: { id: number; username: string; full_name: string };
  gig: {
    gig_uuid: string;
    title: string;
    description: string;
    city: string;
    state: string;
    country: string;
  };
}

interface GigApplicationsListResponse {
  success: boolean;
  message: string;
  counts: { all: number; pending: number; accepted: number; rejected: number };
  data: {
    count: number;
    next: string | null;
    previous: string | null;
    results: GigApplicationListItem[];
  };
}

interface GigApplicationDetailResponse {
  success: boolean;
  message: string;
  data: GigApplicationDetail;
}

/** List gig applications with status, date, and gig filters + pagination */
export const useGetGigApplications = (params: GigApplicationsFilterParams = {}) => {
  return useQuery({
    queryKey: ['gig-applications', params],
    staleTime: 0,
    gcTime: 0,
    queryFn: async () => {
      const sp = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== '' && v !== null) sp.set(k, String(v));
      });
      const qs = sp.toString();
      const { data } = await apiClient.get<GigApplicationsListResponse>(
        `/gigs/applications/${qs ? `?${qs}` : ''}`,
      );
      return data;
    },
  });
};

/** Get a single application's full detail */
export const useGetGigApplication = (applicationUuid: string | undefined) => {
  return useQuery({
    queryKey: ['gig-application', applicationUuid],
    staleTime: 30_000,
    queryFn: async () => {
      const { data } = await apiClient.get<GigApplicationDetailResponse>(
        `/gigs/applications/${applicationUuid}/`,
      );
      return data.data;
    },
    enabled: !!applicationUuid,
  });
};
